import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useLocation } from 'wouter';

interface Video {
  id: number;
  videoUrl: string;
  platform: string;
  embedUrl: string;
  title?: string;
  description?: string;
  category: string;
  tags: string[];
  createdAt: string;
}

// Full-screen video player modal — mobile-first close button
const VideoPlayer = ({ video, onClose }: { video: Video; onClose: () => void }) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/96 z-50 flex flex-col"
      onClick={onClose}
    >
      {/* Close bar — always at top, always tappable */}
      <div className="flex justify-end px-4 py-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium bg-white/10 active:bg-white/20 rounded-full px-4 py-2.5 border border-white/20 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Close
        </button>
      </div>

      {/* Video */}
      <div
        className="flex-1 flex items-center justify-center px-3 sm:px-6 pb-4"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          initial={{ scale: 0.93, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="w-full max-w-4xl"
        >
          <div className="aspect-video rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl bg-black ring-1 ring-white/10">
            <iframe
              src={video.embedUrl}
              title={video.title || 'Video'}
              className="w-full h-full"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
              style={{ border: 'none' }}
            />
          </div>
          {(video.title || video.description) && (
            <div className="mt-3 sm:mt-4 px-1">
              {video.title && <h3 className="text-white text-base sm:text-xl font-bold">{video.title}</h3>}
              {video.description && <p className="text-gray-400 mt-1 text-sm line-clamp-3">{video.description}</p>}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

const PlatformIcon = ({ platform }: { platform: string }) => {
  if (platform === 'youtube') return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
  if (platform === 'vimeo') return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.48 4.807z"/>
    </svg>
  );
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
};

export default function VideoGallerySection() {
  const [, navigate] = useLocation();
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState<Video | null>(null);

  useEffect(() => {
    fetch('/api/videos')
      .then(r => r.json())
      .then(data => { if (data.success) setVideos(data.data || []); })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const featured = [...videos].sort((a, b) => b.id - a.id).slice(0, 4);

  if (isLoading || !featured.length) return null;

  return (
    <section id="video-gallery" className="py-14 sm:py-20 bg-black relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'repeating-linear-gradient(45deg,#ef4444 0,#ef4444 1px,transparent 0,transparent 50%)',
        backgroundSize: '20px 20px'
      }} />

      <div className="container mx-auto px-4 relative z-10">

        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-red-500 text-xs sm:text-sm font-bold uppercase tracking-widest mb-3 block">Watch Us Work</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">Video Gallery</h2>
            <div className="w-16 sm:w-20 h-1 bg-red-600 mx-auto mb-5 sm:mb-6" />
            <p className="text-gray-400 text-sm sm:text-lg max-w-2xl mx-auto px-2">
              See our craftsmanship in action — real projects, real results
            </p>
          </motion.div>
        </div>

        {/* Video Grid — 1 col mobile, 2 col tablet+ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-10 sm:mb-12">
          {featured.map((video, index) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="group relative aspect-video rounded-xl sm:rounded-xl overflow-hidden cursor-pointer bg-gray-900 shadow-xl shadow-black/50 active:scale-[0.98] transition-transform duration-150"
              onClick={() => setActiveVideo(video)}
            >
              {/* Iframe preview (non-interactive) */}
              <div className="absolute inset-0 pointer-events-none">
                <iframe
                  src={video.platform === 'youtube'
                    ? video.embedUrl.replace('?rel=0&modestbranding=1', '?rel=0&modestbranding=1&mute=1')
                    : video.embedUrl}
                  title={video.title || 'Video preview'}
                  className="w-full h-full scale-105"
                  style={{ border: 'none', pointerEvents: 'none' }}
                  tabIndex={-1}
                />
              </div>

              {/* Always-visible overlay — mobile-first design */}
              <div className="absolute inset-0 bg-black/45 group-hover:bg-black/25 transition-colors duration-300 flex flex-col items-center justify-center gap-2.5">
                {/* Large tappable play button */}
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-red-600 shadow-xl shadow-red-900/60 flex items-center justify-center active:scale-95 transition-transform group-hover:scale-105 duration-200">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                {video.title && (
                  <p className="text-white font-semibold text-xs sm:text-base text-center px-5 leading-tight line-clamp-2 max-w-xs">
                    {video.title}
                  </p>
                )}
                <div className="flex items-center gap-1.5 text-white/60 text-xs">
                  <PlatformIcon platform={video.platform} />
                  <span className="capitalize">{video.platform}</span>
                  <span>·</span>
                  <span className="capitalize">{video.category.replace(/-/g, ' ')}</span>
                </div>
              </div>

              {/* Category badge */}
              <div className="absolute top-2.5 left-2.5 bg-red-600/90 backdrop-blur-sm text-white text-[10px] sm:text-xs px-2 py-0.5 sm:py-1 rounded-full font-medium capitalize">
                {video.category.replace(/-/g, ' ')}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA button — full width on mobile */}
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={() => navigate('/gallery?tab=videos')}
            className="w-full sm:w-auto bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-8 sm:px-10 py-3 rounded-full text-base sm:text-lg font-semibold shadow-lg shadow-red-900/30 transition-all duration-200"
          >
            View All Videos
          </Button>
        </div>
      </div>

      {/* Video Player Modal */}
      <AnimatePresence>
        {activeVideo && (
          <VideoPlayer video={activeVideo} onClose={() => setActiveVideo(null)} />
        )}
      </AnimatePresence>
    </section>
  );
}
